/**
 * Recipe Association Card Component
 *
 * Shows recipe status for a POS product and provides actions
 * to create, view, edit, or dissociate a recipe.
 */

import { useState } from 'react';
import { ChefHat, AlertTriangle, Plus, Link, Eye, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { Badge } from '@/components/ui';
import { formatCentimes } from '@/lib/format';
import type { RecipeListItem } from '@bstock/shared';
import { useFormatDate } from '@/hooks';

interface RecipeAssociationCardProps {
  posProductId: string;
  posProductName: string;
  recipe: RecipeListItem | null;
  currency?: string;
  isLoading?: boolean;
  onCreateRecipe?: () => void;
  onAssociateExisting?: () => void;
  onViewRecipe?: () => void;
  onDissociate?: () => void;
}

export function RecipeAssociationCard({
  posProductId: _posProductId,
  posProductName: _posProductName,
  recipe,
  currency = 'EUR',
  isLoading,
  onCreateRecipe,
  onAssociateExisting,
  onViewRecipe,
  onDissociate,
}: RecipeAssociationCardProps) {
  const [showDissociateConfirm, setShowDissociateConfirm] = useState(false);
  const { formatDateShort } = useFormatDate();

  // Format cost using currency
  const formatCost = (cost: number | null) => {
    if (cost === null) return '-';
    return formatCentimes(cost, currency);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-neutral-200 rounded w-24" />
          <div className="h-12 bg-neutral-100 rounded" />
        </div>
      </div>
    );
  }

  // No recipe associated
  if (!recipe) {
    return (
      <div className="rounded-lg border border-dashed border-warning-300 bg-warning-50/50 p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-warning-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-warning-500" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-dark-500">Recette manquante</h4>
            <p className="text-sm text-neutral-600 mt-1">
              Ce produit POS n'est pas encore lie a une recette.
              Creez une recette pour calculer automatiquement le cout theorique.
            </p>
            {(onCreateRecipe || onAssociateExisting) && (
              <div className="flex flex-wrap gap-2 mt-4">
                {onCreateRecipe && (
                  <Button variant="primary" size="sm" onClick={onCreateRecipe}>
                    <Plus className="w-4 h-4" />
                    Creer la recette
                  </Button>
                )}
                {onAssociateExisting && (
                  <Button variant="secondary" size="sm" onClick={onAssociateExisting}>
                    <Link className="w-4 h-4" />
                    Associer existante
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Recipe associated
  return (
    <div className="rounded-lg border border-neutral-200 bg-white">
      <div className="border-b border-neutral-200 px-4 py-3">
        <h3 className="font-medium text-dark-500">Recette liee</h3>
      </div>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <ChefHat className="w-5 h-5 text-primary-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-dark-500 truncate">{recipe.name}</span>
              <Badge variant="neutral">V{recipe.version}</Badge>
            </div>
            <p className="text-sm text-neutral-500 mt-1">
              {recipe.ingredientsCount} ingredients - MAJ: {formatDateShort(recipe.updatedAt)}
            </p>

            {/* Cost display */}
            <div className="mt-3 flex items-center gap-4">
              <div>
                <span className="text-xs text-neutral-500 block">Cout theorique</span>
                <span className="font-semibold text-dark-500">
                  {formatCost(recipe.theoreticalCost)}
                </span>
              </div>
              {recipe.theoreticalCost === null && (
                <Badge variant="warning">PMP incomplet</Badge>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-neutral-100">
          {onViewRecipe && (
            <Button variant="ghost" size="sm" onClick={onViewRecipe}>
              <Eye className="w-4 h-4" />
              Voir la recette
            </Button>
          )}
          <div className="flex-1" />
          {onDissociate && (
            showDissociateConfirm ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-500">Confirmer ?</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDissociateConfirm(false)}
                >
                  Annuler
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    onDissociate();
                    setShowDissociateConfirm(false);
                  }}
                >
                  Dissocier
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDissociateConfirm(true)}
                className="text-neutral-500 hover:text-danger-500"
              >
                <X className="w-4 h-4" />
                Dissocier
              </Button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
